import { useState, useCallback, useEffect } from 'react';

export const useQuestionnaireFiltering = (initialData) => {
  const [filteredData, setFilteredData] = useState(initialData);
  const [activeFilters, setActiveFilters] = useState([]);

  const applyFilters = useCallback((data, filters) => {
    if (!data) return data;
    
    const filtered = {};
    const typeFilter = filters.find(f => ['automated', 'manual'].includes(f));
    const statusFilter = filters.find(f => ['answered', 'unanswered', 'not_applicable'].includes(f));
    const automationStateFilter = filters.find(f => ['issues', 'partial', 'verified'].includes(f));
    const riskFilters = filters.filter(f => ['high', 'medium', 'none'].includes(f));

    Object.keys(data).forEach(section => {
      const filteredQuestions = data[section].filter(question => {
        const isAutomated = question.choices.some(choice => choice.automated === true);
        
        // Apply Type filter first
        if (typeFilter) {
          if (typeFilter === 'automated' && !isAutomated) return false;
          if (typeFilter === 'manual' && isAutomated) return false;
        }

        // Then apply Status filter
        if (statusFilter) {
          if (statusFilter === 'answered' && (!question.risk || question.risk === "UNANSWERED")) return false;
          if (statusFilter === 'unanswered' && question.risk && question.risk !== "UNANSWERED") return false;
          if (statusFilter === 'not_applicable' && question.applicable) return false;
        }

        // Apply Automation State filter
        if (automationStateFilter) {
          const hasError = question.choices?.some(
            (choice) => choice.automationState === "ERROR"
          );
          const hasPartial = question.choices?.some(
            (choice) =>
              choice.automationState === "NOT_AVAILABLE" ||
              choice.automationState === "PARTIAL"
          );
          const hasVerified = question.choices?.some(
            (choice) => choice.automationState === "OKAY"
          );

          // Handle cases where automationState is not available
          // If automated choice has no automationState, check selection status
          const hasNoAutomationState = question.choices?.some(
            (choice) =>
              choice.automated === true &&
              !choice.automationState
          );
          
          let hasNoStateVerified = false;
          let hasNoStateIssues = false;
          
          if (hasNoAutomationState) {
            // Check if any automated choice without automationState is selected
            const hasSelectedWithoutState = question.choices?.some(
              (choice) =>
                choice.automated === true &&
                !choice.automationState &&
                choice.status === "SELECTED"
            );
            
            hasNoStateVerified = hasSelectedWithoutState;
            hasNoStateIssues = !hasSelectedWithoutState;
          }

          if (automationStateFilter === 'issues' && !hasError && !hasNoStateIssues) return false;
          if (automationStateFilter === 'partial' && !hasPartial) return false;
          if (automationStateFilter === 'verified' && !hasVerified && !hasNoStateVerified) return false;
        }

        // Finally apply Risk filters (if any)
        if (riskFilters.length > 0) {
          return riskFilters.some(risk => 
            question.risk === risk.toUpperCase()
          );
        }

        return true;
      });

      filtered[section] = filteredQuestions;
    });

    return filtered;
  }, []);

  // Update filtered data when filters change
  useEffect(() => {
    const newFilteredData = applyFilters(initialData, activeFilters);
    setFilteredData(newFilteredData);
  }, [initialData, activeFilters, applyFilters]);

  // Handler for filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
  }, []);

  return {
    filteredData: filteredData || initialData,
    handleFilterChange,
    activeFilters
  };
}; 